import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  FormControlLabel,
  Switch,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { mapsAPI, apiClient as api } from '../../services/api';
import useWebSocket from '../../hooks/useWebSocket';

const LocationViewer = ({
  open,
  onClose,
  entity,
  entityType // 'patient' or 'staff'
}) => {
  const canvasRef = useRef(null);
  const [realTimeMode, setRealTimeMode] = useState(false);
  const [historyMode, setHistoryMode] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedMapId, setSelectedMapId] = useState('');
  const [availableMaps, setAvailableMaps] = useState([]);
  const [pathPoints, setPathPoints] = useState([]);
  const [zones, setZones] = useState([]);
  const [currentMap, setCurrentMap] = useState(null);
  const [floorPlanImage, setFloorPlanImage] = useState(null);
  
  const { lastMessage } = useWebSocket();

  // Zone colors
  const zoneColors = {
    room: { fill: '#4CAF50', stroke: '#2E7D32' },
    bed: { fill: '#2196F3', stroke: '#1565C0' },
    ward: { fill: '#FF9800', stroke: '#E65100' },
    department: { fill: '#9C27B0', stroke: '#6A1B9A' },
    restricted: { fill: '#F44336', stroke: '#C62828' },
    corridor: { fill: '#607D8B', stroke: '#37474F' },
    elevator: { fill: '#795548', stroke: '#4E342E' },
    exit: { fill: '#E91E63', stroke: '#AD1457' }
  };

  // Entity colors
  const entityColors = {
    patient: '#E91E63',
    staff: '#3F51B5',
    equipment: '#FF5722'
  };

  // Load initial data
  useEffect(() => {
    if (open && entity) {
      loadMapAndZones();
    }
  }, [open, entity]);

  // Handle real-time updates
  useEffect(() => {
    if (lastMessage && realTimeMode && entity?.beacon_id) {
      if (lastMessage.type === 'location_update' && lastMessage.beacon_id === entity.beacon_id) {
        // Update entity position in real-time
        entity.x = lastMessage.location.x;
        entity.y = lastMessage.location.y;
        drawCanvas();
      }
    }
  }, [lastMessage, realTimeMode, entity?.beacon_id]);

  const loadMapAndZones = async () => {
    try {
      // Load maps
      const mapsRes = await mapsAPI.getAll();
      const maps = mapsRes.data?.data || [];
      
      if (maps.length > 0) {
        const defaultMap = maps[0];
        setCurrentMap(defaultMap);
        setSelectedMapId(defaultMap.id);
        loadFloorPlan(defaultMap.url);
        
        // Load zones for this map
        const zonesRes = await api.get(`/zones?map_id=${defaultMap.id}`);
        setZones(zonesRes.data?.data || []);
      }
    } catch (error) {
      console.error('Error loading map and zones:', error);
    }
  };

  const loadFloorPlan = (imageUrl) => {
    if (imageUrl) {
      const img = new Image();
      img.onload = () => {
        setFloorPlanImage(img);
        setTimeout(() => drawCanvas(), 100);
      };
      img.crossOrigin = 'anonymous';
      img.src = imageUrl;
    }
  };

  const loadLocationHistory = async () => {
    if (!entity?.beacon_id || !startDate || !endDate) return;
    
    try {
      const response = await api.get('/location-history', {
        params: {
          beacon_id: entity.beacon_id,
          start_time: startDate,
          end_time: endDate
        }
      });
      
      if (response.data?.success) {
        setAvailableMaps(response.data.data.maps || []);
        setPathPoints(response.data.data.path || []);
        
        if (!selectedMapId && response.data.data.maps.length > 0) {
          setSelectedMapId(response.data.data.maps[0].map_id);
        }
      }
    } catch (error) {
      console.error('Error loading location history:', error);
    }
  };

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const { width, height } = canvas;
    
    // Clear canvas
    ctx.clearRect(0, 0, width, height);
    
    // Draw grid background
    drawGrid(ctx, width, height);
    
    // Draw floor plan
    if (floorPlanImage) {
      drawFloorPlan(ctx);
    }
    
    // Draw zones
    zones.forEach(zone => drawZone(ctx, zone));
    
    // Draw path for history mode
    if (historyMode && pathPoints.length > 0) {
      drawPath(ctx);
    }
    
    // Draw entity current position
    if (entity && entity.x && entity.y) {
      drawEntity(ctx, entity);
    }
  }, [zones, pathPoints, entity, floorPlanImage, historyMode]);

  const drawGrid = (ctx, width, height) => {
    const gridSize = 20;
    ctx.strokeStyle = '#e0e0e0';
    ctx.lineWidth = 1;
    
    ctx.fillStyle = '#fafafa';
    ctx.fillRect(0, 0, width, height);
    
    for (let x = 0; x <= width; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }
    
    for (let y = 0; y <= height; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }
  };

  const drawFloorPlan = (ctx) => {
    if (!floorPlanImage) return;
    
    const canvasWidth = ctx.canvas.width;
    const canvasHeight = ctx.canvas.height;
    const imgWidth = floorPlanImage.width;
    const imgHeight = floorPlanImage.height;
    
    const scale = Math.min(canvasWidth / imgWidth, canvasHeight / imgHeight);
    const scaledWidth = imgWidth * scale;
    const scaledHeight = imgHeight * scale;
    
    const x = (canvasWidth - scaledWidth) / 2;
    const y = (canvasHeight - scaledHeight) / 2;
    
    ctx.globalAlpha = 0.8;
    ctx.drawImage(floorPlanImage, x, y, scaledWidth, scaledHeight);
    ctx.globalAlpha = 1;
  };

  const drawZone = (ctx, zone) => {
    let coords = zone.coordinates;
    if (!coords) return;
    
    if (typeof coords === 'string') {
      try {
        coords = JSON.parse(coords);
      } catch (e) {
        return;
      }
    }
    
    const colors = zoneColors[zone.zone_type] || zoneColors.room;
    
    ctx.fillStyle = colors.fill;
    ctx.strokeStyle = colors.stroke;
    ctx.globalAlpha = 0.3;
    ctx.lineWidth = 2;
    
    if (coords.type === 'rectangle') {
      ctx.fillRect(coords.x, coords.y, coords.width, coords.height);
      ctx.strokeRect(coords.x, coords.y, coords.width, coords.height);
    } else if (coords.type === 'circle') {
      ctx.beginPath();
      ctx.arc(coords.x, coords.y, coords.radius, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();
    } else if (coords.type === 'polygon' && coords.points) {
      ctx.beginPath();
      ctx.moveTo(coords.points[0].x, coords.points[0].y);
      coords.points.forEach(point => ctx.lineTo(point.x, point.y));
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }
    
    // Draw zone label
    ctx.globalAlpha = 1;
    ctx.fillStyle = '#000';
    ctx.font = '12px Arial';
    ctx.textAlign = 'center';
    
    const centerX = coords.center_point?.x || coords.x + (coords.width || 0) / 2;
    const centerY = coords.center_point?.y || coords.y + (coords.height || 0) / 2;
    
    ctx.fillText(zone.zone_code, centerX, centerY - 5);
    ctx.font = '10px Arial';
    ctx.fillText(zone.zone_name, centerX, centerY + 8);
  };

  const drawPath = (ctx) => {
    if (pathPoints.length < 2) return;
    
    // Draw path lines
    ctx.strokeStyle = '#2196f3';
    ctx.lineWidth = 3;
    ctx.globalAlpha = 0.7;
    
    ctx.beginPath();
    ctx.moveTo(pathPoints[0].x, pathPoints[0].y);
    pathPoints.forEach(point => ctx.lineTo(point.x, point.y));
    ctx.stroke();
    
    // Draw start point
    ctx.fillStyle = '#4caf50';
    ctx.globalAlpha = 1;
    ctx.beginPath();
    ctx.arc(pathPoints[0].x, pathPoints[0].y, 8, 0, 2 * Math.PI);
    ctx.fill();
    
    // Draw end point
    ctx.fillStyle = '#f44336';
    ctx.beginPath();
    ctx.arc(pathPoints[pathPoints.length - 1].x, pathPoints[pathPoints.length - 1].y, 8, 0, 2 * Math.PI);
    ctx.fill();
  };

  const drawEntity = (ctx, entity) => {
    const x = entity.x || 0;
    const y = entity.y || 0;
    const color = entityColors[entityType] || '#FF5722';
    
    // Draw entity circle
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.9;
    ctx.beginPath();
    ctx.arc(x, y, 12, 0, 2 * Math.PI);
    ctx.fill();
    
    // Draw border
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 3;
    ctx.stroke();
    
    // Pulse animation for real-time mode
    if (realTimeMode) {
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.globalAlpha = 0.5;
      ctx.beginPath();
      ctx.arc(x, y, 20, 0, 2 * Math.PI);
      ctx.stroke();
    }
    
    // Draw label
    ctx.globalAlpha = 1;
    ctx.fillStyle = '#000';
    ctx.font = '12px Arial';
    ctx.textAlign = 'center';
    const label = entity.medical_record_number || entity.staff_code || entity.equipmentName || entity.name || 'Unknown';
    ctx.fillText(label, x, y - 20);
  };

  // Redraw canvas when data changes
  useEffect(() => {
    if (open) {
      drawCanvas();
    }
  }, [drawCanvas, open]);

  const handleClose = () => {
    setRealTimeMode(false);
    setHistoryMode(false);
    setPathPoints([]);
    setAvailableMaps([]);
    onClose();
  };

  const getEntityTitle = () => {
    if (entityType === 'patient') {
      return `👤 ${entity?.medical_record_number || 'Paciente'}`;
    }
    if (entityType === 'staff') {
      return `👨💼 ${entity?.staff_code || 'Funcionário'}`;
    }
    return `🔧 ${entity?.equipmentName || 'Equipamento'}`;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            📍 {getEntityTitle()}
          </Typography>
          <Box display="flex" gap={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={realTimeMode}
                  onChange={(e) => setRealTimeMode(e.target.checked)}
                  disabled={historyMode}
                />
              }
              label="Tempo Real"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={historyMode}
                  onChange={(e) => setHistoryMode(e.target.checked)}
                  disabled={realTimeMode}
                />
              }
              label="Histórico"
            />
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {/* History Controls */}
        {historyMode && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.100' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Data/Hora Início"
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Data/Hora Fim"
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Mapa</InputLabel>
                  <Select
                    value={selectedMapId}
                    onChange={(e) => setSelectedMapId(e.target.value)}
                    label="Mapa"
                  >
                    {availableMaps.map(map => (
                      <MenuItem key={map.map_id} value={map.map_id}>
                        {map.area_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Button
                  variant="contained"
                  startIcon={<HistoryIcon />}
                  onClick={loadLocationHistory}
                  disabled={!startDate || !endDate}
                  fullWidth
                >
                  Carregar
                </Button>
              </Grid>
            </Grid>
            {pathPoints.length > 0 && (
              <Box mt={2}>
                <Typography variant="body2" color="textSecondary">
                  {pathPoints.length} pontos encontrados | {availableMaps.length} mapa(s) diferentes
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        {/* Legend */}
        <Paper sx={{ p: 1, mb: 2 }}>
          <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
            Legenda
          </Typography>
          <Chip
            icon={<Box sx={{ width: 12, height: 12, bgcolor: entityColors[entityType], borderRadius: '50%' }} />}
            label={entityType === 'patient' ? 'Paciente' : entityType === 'staff' ? 'Funcionário' : 'Equipamento'}
            size="small"
            sx={{ mr: 1 }}
          />
          <Chip
            icon={<Box sx={{ width: 12, height: 12, bgcolor: zoneColors.room.fill, borderRadius: '20%' }} />}
            label="Zonas"
            size="small"
            sx={{ mr: 1 }}
          />
          {historyMode && pathPoints.length > 0 && (
            <>
              <Chip
                icon={<Box sx={{ width: 12, height: 12, bgcolor: '#4caf50', borderRadius: '50%' }} />}
                label="Início"
                size="small"
                sx={{ mr: 1 }}
              />
              <Chip
                icon={<Box sx={{ width: 12, height: 12, bgcolor: '#f44336', borderRadius: '50%' }} />}
                label="Fim"
                size="small"
              />
            </>
          )}
        </Paper>

        {/* Canvas */}
        <Box position="relative" display="inline-block" width="100%">
          <canvas
            ref={canvasRef}
            width={800}
            height={600}
            style={{
              border: '2px solid #e0e0e0',
              borderRadius: '8px',
              width: '100%',
              height: 'auto',
              maxWidth: '800px'
            }}
          />
        </Box>

        {/* Entity Info */}
        <Box mt={2}>
          <Typography variant="body2">
            <strong>Posição:</strong> ({(entity?.x || 0).toFixed(1)}, {(entity?.y || 0).toFixed(1)})
          </Typography>
          {entityType === 'patient' && (
            <>
              <Typography variant="body2">
                <strong>Prontuário:</strong> {entity?.medical_record_number || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Quarto:</strong> {entity?.current_room || 'N/A'}
              </Typography>
            </>
          )}
          {entityType === 'staff' && (
            <>
              <Typography variant="body2">
                <strong>Matrícula:</strong> {entity?.staff_code || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Função:</strong> {entity?.role || 'N/A'}
              </Typography>
            </>
          )}
          {entityType === 'equipment' && (
            <>
              <Typography variant="body2">
                <strong>Tipo:</strong> {entity?.equipmentType || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Categoria:</strong> {entity?.category || 'N/A'}
              </Typography>
            </>
          )}
          
          {realTimeMode && (
            <Box mt={1} display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#4caf50',
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                    '100%': { opacity: 1 }
                  }
                }}
              />
              <Typography variant="caption" color="success.main">
                Rastreamento em tempo real ativo
              </Typography>
            </Box>
          )}
          
          {historyMode && pathPoints.length > 0 && (
            <Box mt={1}>
              <Typography variant="caption" color="primary">
                Histórico: {pathPoints.length} pontos | {availableMaps.length} mapa(s)
              </Typography>
              <br />
              <Typography variant="caption" color="textSecondary">
                🟢 Início | 🔴 Fim | — Caminho percorrido
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default LocationViewer;